import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import {
  type AccountOpeningCollection,
  type AccountOpeningCollectionStep,
  type AccountOpeningConfirmScope,
  type AccountOpeningItemCopy,
  type AccountOpeningPanel,
  type AccountOpeningSchemaFormPanel,
  type AccountOpeningSchemaKey,
  type AccountOpeningStep,
  type AccountOpeningStepId,
} from '../../../models/AccountOpeningDescriptor'
import { accountHeading } from '../descriptor/stepCopy'
import { applyPanelValues, hasProperties, schemaProperties, sliceSchema } from './schemaValues'
import { roleLabelIn } from './accountTypeCatalog'
import { axisMissingItems, holderConflictItems, holderMissingItems, schemaInvalidItems, schemaMissingItems, type MissingItem } from './missingFields'
import { partySchemaRole } from './parties'
import { accountPrefillSources, partyPrefillSources } from './prefillSources'
import { type ItemPatch, type WizardAccount, type WizardContext, type WizardParty } from './types'

interface ResolvedItemBase {
  ref: string
  heading: string
  subheading?: string
  schemaNote?: string
}

export type ResolvedItem =
  | (ResolvedItemBase & { collection: 'accounts'; account: WizardAccount })
  | (ResolvedItemBase & { collection: 'parties'; party: WizardParty })

export interface CollectionContext {
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  accounts: WizardAccount[]
  parties: WizardParty[]
  readOnly: boolean
  copy: Required<AccountOpeningItemCopy>
}

export const SCHEMA_KEYS: readonly AccountOpeningSchemaKey[] = ['accountTypeId', 'role']

const asSchemaKey = (value: string | undefined): AccountOpeningSchemaKey | undefined => SCHEMA_KEYS.find((key) => key === value)

export const schemaValueSource = (panel: AccountOpeningSchemaFormPanel): AccountOpeningSchemaKey | undefined =>
  panel.valueFrom !== undefined ? asSchemaKey(panel.valueFrom) : asSchemaKey(panel.keyedBy)

export const flowCollections = (steps: AccountOpeningStep[]): AccountOpeningCollection[] => [
  ...new Set(steps.flatMap((step) => (step.kind === 'collection' ? [step.collection] : []))),
]

export const collectionEntrySteps = (steps: AccountOpeningStep[]): Map<AccountOpeningCollection, AccountOpeningStepId> => {
  const entry = new Map<AccountOpeningCollection, AccountOpeningStepId>()
  steps.forEach((step) => {
    if (step.kind === 'collection' && !entry.has(step.collection)) {
      entry.set(step.collection, step.id)
    }
  })
  return entry
}

export const itemsOf = (collection: AccountOpeningCollection, ctx: CollectionContext): ResolvedItem[] => {
  if (collection === 'parties') {
    return ctx.parties.map((party) => ({
      collection: 'parties' as const,
      ref: party.customerRefId,
      heading: party.name,
      subheading: party.roleCodes.map((code) => roleLabelIn(ctx.accountTypes, code)).join(', '),
      schemaNote:
        party.roleCodes.length > 1 ? ctx.copy.partyRoleNote.replace('{role}', roleLabelIn(ctx.accountTypes, partySchemaRole(party) ?? '')) : undefined,
      party,
    }))
  }
  return ctx.accounts.map((account, index) => ({
    collection: 'accounts' as const,
    ref: account.accountRefId,
    heading: accountHeading(index, ctx.readOnly, ctx.copy),
    account,
  }))
}

const rawSchema = (item: ResolvedItem): Record<string, unknown> => (item.collection === 'accounts' ? item.account.schemaSnapshot : item.party.kycSchemaSnapshot)

export const itemSchemaFetchedAt = (item: ResolvedItem): string | undefined => (item.collection === 'accounts' ? item.account.schemaFetchedAt : undefined)

export const itemValues = (item: ResolvedItem): Record<string, unknown> => (item.collection === 'accounts' ? item.account.values : item.party.kycValues)

export const panelSchema = (item: ResolvedItem, panel: AccountOpeningSchemaFormPanel): Record<string, unknown> => sliceSchema(rawSchema(item), panel)

const panelHasFields = (item: ResolvedItem, panel: AccountOpeningSchemaFormPanel): boolean => hasProperties(panelSchema(item, panel))

export const applyPanelChange = (
  item: ResolvedItem,
  panel: AccountOpeningSchemaFormPanel,
  formData: Record<string, unknown> | undefined,
): Record<string, unknown> => applyPanelValues(itemValues(item), formData, rawSchema(item), panel)

const panelMissingItems = (item: ResolvedItem, panel: AccountOpeningPanel, ctx: CollectionContext): MissingItem[] => {
  switch (panel.kind) {
    case 'typeSummary':
      return item.collection === 'accounts' ? axisMissingItems(ctx.selectors, item.account) : []
    case 'holders':
      return item.collection === 'accounts' ? [...holderMissingItems(ctx.accountTypes, item.account), ...holderConflictItems(item.account)] : []
    case 'schemaForm':
      return schemaMissingItems(panelSchema(item, panel), itemValues(item), `${item.collection}:${item.ref}`)
  }
}

export const itemMissingItems = (step: AccountOpeningCollectionStep, item: ResolvedItem, ctx: CollectionContext): MissingItem[] => {
  const items = (step.panels ?? []).flatMap((panel) => panelMissingItems(item, panel, ctx))
  if (item.collection === 'parties' && !item.party.name.trim()) {
    return [{ key: 'name', label: ctx.copy.partyNameLabel }, ...items]
  }
  return items
}

const isItemComplete = (step: AccountOpeningCollectionStep, item: ResolvedItem, ctx: CollectionContext): boolean =>
  itemMissingItems(step, item, ctx).length === 0

export interface ItemGroup {
  key: string
  heading: string
  items: MissingItem[]
}

const groupBy = (step: AccountOpeningCollectionStep, ctx: CollectionContext, itemsOfGroup: (item: ResolvedItem) => MissingItem[]): ItemGroup[] =>
  itemsOf(step.collection, ctx)
    .map((item) => ({ key: item.ref, heading: item.heading, items: itemsOfGroup(item) }))
    .filter((group) => group.items.length > 0)

export const stepMissingGroups = (step: AccountOpeningCollectionStep, ctx: CollectionContext): ItemGroup[] =>
  groupBy(step, ctx, (item) => itemMissingItems(step, item, ctx))

const itemInvalidItems = (step: AccountOpeningCollectionStep, item: ResolvedItem): MissingItem[] =>
  (step.panels ?? []).flatMap((panel) =>
    panel.kind === 'schemaForm' ? schemaInvalidItems(panelSchema(item, panel), itemValues(item), `${item.collection}:${item.ref}`) : [],
  )

export const stepInvalidGroups = (step: AccountOpeningCollectionStep, ctx: CollectionContext): ItemGroup[] =>
  groupBy(step, ctx, (item) => itemInvalidItems(step, item))

export const isStepComplete = (step: AccountOpeningCollectionStep, ctx: CollectionContext): boolean => {
  const items = itemsOf(step.collection, ctx)
  return items.length > 0 && items.every((item) => isItemComplete(step, item, ctx))
}

const prefillSchema = (step: AccountOpeningCollectionStep, item: ResolvedItem): Record<string, unknown> => {
  const panel = (step.panels ?? []).find((candidate): candidate is AccountOpeningSchemaFormPanel => candidate.kind === 'schemaForm')
  return panel ? panelSchema(item, panel) : rawSchema(item)
}

export const itemFillable = (step: AccountOpeningCollectionStep, item: ResolvedItem): boolean =>
  (step.panels ?? []).some((panel) => panel.kind === 'schemaForm' && panelHasFields(item, panel))

export const prefillWanted = (step: AccountOpeningCollectionStep, item: ResolvedItem): boolean => {
  const asked = Object.keys(schemaProperties(prefillSchema(step, item)))
  return asked.length > 0 && !asked.some((name) => itemValues(item)[name] !== undefined)
}

export const prefillSourcesFor = (item: ResolvedItem, ctx: WizardContext, step: AccountOpeningCollectionStep): Record<string, unknown> => {
  const schema = prefillSchema(step, item)
  return item.collection === 'accounts' ? accountPrefillSources(ctx, item.account, schema) : partyPrefillSources(ctx, item.party, schema)
}

export const collectionSchemaSource = (
  steps: AccountOpeningStep[],
  collection: AccountOpeningCollection,
): { entity: string; keyedBy: AccountOpeningSchemaFormPanel['keyedBy']; valueFrom: ReturnType<typeof schemaValueSource> } | undefined => {
  for (const step of steps) {
    if (step.kind !== 'collection' || step.collection !== collection) {
      continue
    }
    const panel = (step.panels ?? []).find((candidate): candidate is AccountOpeningSchemaFormPanel => candidate.kind === 'schemaForm')
    if (panel) {
      return { entity: panel.entity, keyedBy: panel.keyedBy, valueFrom: schemaValueSource(panel) }
    }
  }
  return undefined
}

export const stepsKeyedBy = (
  steps: AccountOpeningStep[],
  collection: AccountOpeningCollection,
  valueFrom: ReturnType<typeof schemaValueSource>,
): AccountOpeningStepId[] =>
  steps
    .filter(
      (step) =>
        step.kind === 'collection' &&
        step.collection === collection &&
        (step.panels ?? []).some((panel) => panel.kind === 'schemaForm' && schemaValueSource(panel) === valueFrom),
    )
    .map((step) => step.id)

export const confirmScopes = (
  steps: AccountOpeningStep[],
  item: ResolvedItem,
  patch: ItemPatch,
  stepId: AccountOpeningStepId,
): Record<AccountOpeningStepId, AccountOpeningConfirmScope | undefined> => {
  const affected = new Set<AccountOpeningStepId>([stepId])
  if (patch.typeId !== undefined) {
    stepsKeyedBy(steps, item.collection, 'accountTypeId').forEach((keyed) => affected.add(keyed))
  }
  return Object.fromEntries(
    [...affected].map((affectedId) => {
      const step = steps.find((candidate) => candidate.id === affectedId)
      return [affectedId, step?.kind === 'collection' ? step.confirm?.scope : undefined]
    }),
  )
}

export const itemSchemaKey = (item: ResolvedItem, valueFrom: ReturnType<typeof schemaValueSource>): string => {
  switch (valueFrom) {
    case 'accountTypeId':
      return item.collection === 'accounts' ? item.account.typeId : ''
    case 'role':
      return item.collection === 'parties' ? (partySchemaRole(item.party) ?? '') : ''
    default:
      return ''
  }
}

export const itemNeedsSchema = (item: ResolvedItem, valueFrom: ReturnType<typeof schemaValueSource>): boolean =>
  !!itemSchemaKey(item, valueFrom) && Object.keys(rawSchema(item)).length === 0

export const preparationSignature = (steps: AccountOpeningStep[], collection: AccountOpeningCollection, ctx: CollectionContext): string => {
  const source = collectionSchemaSource(steps, collection)
  if (!source) {
    return ''
  }
  return itemsOf(collection, ctx)
    .filter((item) => itemNeedsSchema(item, source.valueFrom))
    .map((item) => `${item.ref}:${itemSchemaKey(item, source.valueFrom)}`)
    .sort()
    .join('|')
}
